import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  StyleSheet, View, Text, ScrollView,
} from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import * as Clipboard from 'expo-clipboard';
import { FontAwesome } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { AssetInstance, UserPermission } from '../../../../../constants/Types';
import Style from '../../../../../constants/Style';
import Colors from '../../../../../constants/Colors';
import PressableStyled from '../../../../../components/PressableStyled';
import Button from '../../../../../components/Button';
import BinIcon12 from '../../../../../components/12pxIcons/BinIcon12';
import CopyIcon16 from '../../../../../components/16pxIcons/CopyIcon16';
import { COPIED_TO_CLIPBOARD, DELETE_SOLVE_TAG_SUCCESS } from '../../../../../constants/AlertMessages';
import { logEvent } from '../../../../../components/LogEvent';
import deleteSolveTag from '../../../../../requests/Assets/DeleteSolveTag';
import isMobile from '../../../../../components/IsMobile';
import CustomTextField from '../../../../../components/CustomTextField';
import PlusIcon from '../../../../../components/16pxIcons/PlusIcon';
import AddQRCodeModal from '../../../../../components/AssetManagement/AddQRCodeModal';

interface TagSectionProps {
  asset: AssetInstance,
  showScreenAlert,
  setLoading: Dispatch<SetStateAction<boolean>>,
  domain: string,
  addTag,
}

const TagSection = ({
  asset, showScreenAlert, setLoading, domain, addTag,
}: TagSectionProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const [tagIndex, setTagIndex] = useState<number>(0);
  const [addQRCodeModalShown, setAddQRCodeModalShown] = useState<boolean>(false);
  const tags: string[] = asset?.solveTagIds?.length > 0 ? asset.solveTagIds : [asset.id];
  const currentTagId: string = tags[tagIndex];
  const tagUrl: string = `${domain}${currentTagId}`;
  const qrUrl: string = `${tagUrl}?ss=QR`;
  const nfcUrl: string = `${tagUrl}?ss=NFC`;
  const canEditAsset: boolean = isAdmin
    || permissions.includes(UserPermission.ASSETS_UPDATE);

  const copyQR: () => void = () => {
    Clipboard.setStringAsync(qrUrl);
    showScreenAlert(COPIED_TO_CLIPBOARD, null, false);
  };

  const copyNFC: () => void = () => {
    Clipboard.setStringAsync(nfcUrl);
    showScreenAlert(COPIED_TO_CLIPBOARD, null, false);
  };

  const deleteError = (errorText?: string) => showScreenAlert(
    'Error',
    errorText ?? 'Unable to remove Solve tag at the moment',
  );

  const removeSolveTag: () => void = () => {
    logEvent('AssetRow', 'delete_tag');
    showScreenAlert(
      'Delete solve tag?',
      <Text>
        {'Do you want to delete this tag - '}
        <Text style={Style.alertBoldText}>
          {currentTagId}
        </Text>?
      </Text>,
      true,
      () => {
        logEvent('AssetRow', 'delete_tag_confirm');
        setLoading(true);
        deleteSolveTag(
          asset.id,
          currentTagId,
        ).then((response) => {
          if (response && !response?.isError) {
            tags.splice(tagIndex, 1);
            if (tagIndex >= tags.length - 1) {
              setTagIndex(tagIndex - 1);
            }
            showScreenAlert(
              'Success',
              DELETE_SOLVE_TAG_SUCCESS,
              false,
            );
          } else deleteError(response?.errorText);
        }).catch((error) => {
          deleteError(error?.errorText);
        }).finally(() => setLoading(false));
      },
      'Delete',
    );
  };

  const previousButtonDisabled: boolean = tagIndex <= 0;
  const nextButtonDisabled: boolean = tagIndex >= (tags?.length ?? 0) - 1;

  return <ScrollView
    style={styles.container}
    testID='assets-tag-section'
  >
    <View style={styles.header}>
      <QRCode
        size={178}
        value={qrUrl}
      />
      <View style={styles.tagControls}>
        <Text style={styles.label}>{`ID: ${currentTagId}`}</Text>
        <View style={Style.row}>
          <PressableStyled
            style={[
              styles.indexButton,
              previousButtonDisabled ? styles.disabledButton : styles.enabledButton,
            ]}
            maintainOpacityWhenDisabled
            disabled={previousButtonDisabled}
            onPress={() => setTagIndex(Math.max(0, tagIndex - 1))}
            testID='previous-tag-btn'
          >
            <FontAwesome
              name='angle-left'
              color={previousButtonDisabled
                ? Colors.light.hereworksBlack400 : Colors.light.hereworksBlack600}
              size={24}
            />
          </PressableStyled>
          <Text style={styles.pageNumber}>{`${tagIndex + 1}/${tags?.length ?? 1}`}</Text>
          <PressableStyled
            style={[
              styles.indexButton,
              nextButtonDisabled ? styles.disabledButton : styles.enabledButton,
            ]}
            maintainOpacityWhenDisabled
            disabled={nextButtonDisabled}
            onPress={() => setTagIndex(Math.min((tags?.length ?? 0) - 1, tagIndex + 1))}
            testID='next-tag-btn'
          >
            <FontAwesome
              name='angle-right'
              color={nextButtonDisabled
                ? Colors.light.hereworksBlack400 : Colors.light.hereworksBlack600}
              size={24}
            />
          </PressableStyled>
        </View>
      </View>
      {isMobile && <View>
        <CustomTextField
          title='QR link'
          value={qrUrl}
          copy={copyQR}
        />
        <CustomTextField
          title='NFC link'
          value={nfcUrl}
          copy={copyNFC}
        />
        {canEditAsset && <View style={styles.addTagButtonContainer}>
          <Button
            title='Add another tag'
            onPress={() => setAddQRCodeModalShown(true)}
            children={<PlusIcon style={styles.addTagButtonIcon} />}
            style={styles.addTagButton}
            affirmative
          />
          <PressableStyled
            style={styles.deleteButton}
            onPress={removeSolveTag}
            disabled={!(tags?.length > 1)}
            testID='delete-tag-btn'
          >
            <BinIcon12 />
          </PressableStyled>
        </View>}
      </View>}
    </View>
    {!isMobile && <View style={styles.buttonContainer}>
      <View style={Style.row}>
        <Button
          title='Copy QR link'
          children={<CopyIcon16 style={styles.copyIcon} />}
          style={styles.copyButton}
          onPress={copyQR}
          testID='copy-qr-link-btn'
        />
        <Button
          title='Copy NFC link'
          children={<CopyIcon16 style={styles.copyIcon} />}
          style={styles.copyButton}
          onPress={copyNFC}
          testID='copy-nfc-link-btn'
        />
      </View>
      {canEditAsset && <PressableStyled
        style={styles.deleteButton}
        onPress={removeSolveTag}
        disabled={!(tags?.length > 1)}
        testID='delete-tag-btn'
      >
        <BinIcon12 />
      </PressableStyled>}
    </View>}
    <AddQRCodeModal
      {...{ addQRCodeModalShown, tags }}
      hideQRCodeModal={() => setAddQRCodeModalShown(false)}
      assetId={asset.id}
      addTag={(tagId: string) => {
        addTag(tagId);
        setTagIndex(tags.length);
      }}
    />
  </ScrollView>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 16,
    marginHorizontal: -16,
  },
  header: {
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: 'center',
    paddingVertical: isMobile ? 16 : 8,
    paddingHorizontal: isMobile ? 16 : undefined,
    marginBottom: isMobile ? 8 : 24,
    marginTop: isMobile ? 16 : undefined,
    borderWidth: isMobile ? 1 : undefined,
    borderRadius: 16,
    borderColor: Colors.light.hereworksBlue50,
    backgroundColor: '#FFF',
  },
  tagControls: {
    flex: isMobile ? undefined : 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  label: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: isMobile ? 8 : 16,
    marginTop: isMobile ? 12 : undefined,
    marginStart: 12,
  },
  indexButton: {
    width: 36,
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 100,
    padding: 6,
    marginHorizontal: 18,
    borderColor: Colors.light.hereworksWhite500,
    backgroundColor: '#FFF',
  },
  enabledButton: {
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
    shadowOffset: { width: 2, height: 4 },
    shadowColor: '#323247',
  },
  disabledButton: {
    borderColor: Colors.light.hereworksBlack100,
    backgroundColor: Colors.light.hereworksBlack50,
  },
  pageNumber: {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  buttonContainer: {
    ...Style.row,
    marginBottom: 24,
  },
  copyButton: {
    paddingVertical: 5,
    height: 32,
    flexDirection: 'row-reverse',
    marginEnd: 8,
  },
  copyIcon: {
    marginEnd: 8,
  },
  deleteButton: {
    width: 32,
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksRed500,
    borderRadius: 4,
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
    shadowOffset: { width: 2, height: 4 },
    shadowColor: '#323247',
  },
  addTagButtonContainer: {
    ...Style.row,
    marginTop: 24,
  },
  addTagButton: {
    flex: undefined,
    marginStart: undefined,
    paddingVertical: 0,
    height: 32,
  },
  addTagButtonIcon: {
    marginStart: 4,
  },
});

export default TagSection;
